import {
  AlertTriangle,
  Inbox,
  KeyRound,
  Loader2,
  ServerOff,
  WifiOff,
} from "lucide-react";
import type { ReactNode } from "react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";

export function Spinner({ className }: { className?: string }) {
  return (
    <Loader2 className={cn("size-4 animate-spin", className)} aria-hidden />
  );
}

export function LoadingRows({ rows = 4 }: { rows?: number }) {
  return (
    <div className="space-y-2" aria-busy="true" aria-label="Loading">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-11 w-full" />
      ))}
    </div>
  );
}

function Panel({
  icon,
  title,
  children,
  action,
}: {
  icon: ReactNode;
  title: string;
  children?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed px-6 py-12 text-center">
      <div className="text-muted-foreground">{icon}</div>
      <div className="space-y-1">
        <p className="font-medium">{title}</p>
        {children ? (
          <p className="text-muted-foreground mx-auto max-w-md text-sm">
            {children}
          </p>
        ) : null}
      </div>
      {action}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <Panel icon={<Inbox className="size-8" />} title={title} action={action}>
      {description}
    </Panel>
  );
}

/**
 * Render an ApiError with a message appropriate to its status.
 *
 * The distinctions matter operationally: status 0 means the API is unreachable
 * (which has happened in production while this static site stayed up), 401 means
 * the key is wrong, and 403 means the key is fine but the resource is gated.
 * Collapsing them into one message made the API being down look like a bad key.
 */
export function ErrorState({
  error,
  onRetry,
}: {
  error: ApiError;
  onRetry?: () => void;
}) {
  const retry = onRetry ? (
    <Button variant="outline" size="sm" onClick={onRetry}>
      Try again
    </Button>
  ) : undefined;

  if (error.status === 0) {
    return (
      <Panel
        icon={<WifiOff className="size-8" />}
        title="Cannot reach the server"
        action={retry}
      >
        {error.message}
      </Panel>
    );
  }

  if (error.isNotConfigured) {
    return (
      <Panel
        icon={<ServerOff className="size-8" />}
        title="Admin API not configured"
        action={retry}
      >
        {error.message}
      </Panel>
    );
  }

  if (error.isUnauthorized) {
    // No retry button: retrying without a key produces the same 401, so offering it
    // invites a student to click it repeatedly and conclude the site is broken.
    //
    // The server's own message is deliberately not shown. It is written for someone
    // calling the API directly, so it explains the x-api-key header and suggests curl --
    // advice that makes no sense to somebody already looking at the site, where the answer
    // is the Sign in button in the header.
    return (
      <Panel icon={<KeyRound className="size-8" />} title="Sign in required">
        Use <span className="text-foreground font-medium">Sign in</span> at the top of
        the page. This challenge is visible only to students of its course.
      </Panel>
    );
  }

  return (
    <Panel
      icon={<AlertTriangle className="size-8" />}
      title={error.isForbidden ? "Not available" : "Something went wrong"}
      action={retry}
    >
      {error.message}
    </Panel>
  );
}

/** Convenience wrapper: loading, then error, then content. */
export function AsyncBoundary<T>({
  state,
  children,
  loading,
  empty,
}: {
  state: { data: T | null; error: ApiError | null; loading: boolean; refetch: () => void };
  children: (data: T) => ReactNode;
  loading?: ReactNode;
  empty?: ReactNode;
}) {
  if (state.error) return <ErrorState error={state.error} onRetry={state.refetch} />;
  if (state.data === null) return <>{state.loading ? (loading ?? <LoadingRows />) : null}</>;
  if (empty && Array.isArray(state.data) && state.data.length === 0) return <>{empty}</>;
  return <>{children(state.data)}</>;
}
